import React from 'react'
import { Col, Row, Modal, notification } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  AssignTree,
  PolicyApprovalExceptionTrees,
  PolicyApprovalRules,
  PolicyConferenceExceptionTrees,
  PolicyConferenceRules
} from 'src/types'
import { NodeColor } from 'src/components/Tree/Nodes'
import { Tree } from 'src/components/Tree'
import * as PoliciesService from 'src/services/api/policies-flow'
import isThisType from 'src/helpers/is-this-ype'

import * as CSS from './styles'

import { PropsTreeSelected } from '../..'

type AssignTreePick = Pick<AssignTree, 'assign_tree_id' | 'color' | 'disabled'>

type PolicyExceptionTrees =
  | PolicyApprovalExceptionTrees
  | PolicyConferenceExceptionTrees

interface Props {
  selectedRule: PolicyApprovalRules | PolicyConferenceRules
  toggle(update?: boolean): void
  treesSelected: PropsTreeSelected
}

export default function HierarchyFlowsBindExceptionStructure({
  selectedRule,
  toggle,
  treesSelected
}: Props) {
  const { t } = useTranslation()

  if (!selectedRule) {
    return <></>
  }

  const valuedNodes: AssignTreePick[] = [
    {
      assign_tree_id: treesSelected.id,
      color: NodeColor.DISABLED,
      disabled: true
    }
  ]

  if (
    isThisType<PolicyApprovalRules>(
      selectedRule,
      'policy_approval_exception_trees'
    )
  ) {
    valuedNodes.push(
      ...selectedRule.policy_approval_exception_trees.map(tree => ({
        assign_tree_id: tree.assign_tree_id,
        color: NodeColor.SELECTED
      }))
    )
  } else {
    valuedNodes.push(
      ...selectedRule.policy_conference_exception_trees.map(tree => ({
        assign_tree_id: tree.assign_tree_id,
        color: NodeColor.SELECTED
      }))
    )
  }

  function setTreeSelected(treeSelected: PropsTreeSelected) {
    let content
    let found: PolicyExceptionTrees
    let policy_exception_trees: PolicyExceptionTrees

    if (
      isThisType<PolicyApprovalRules>(
        selectedRule,
        'policy_approval_exception_trees'
      )
    ) {
      found = selectedRule.policy_approval_exception_trees.find(
        node => `${node.assign_tree_id}` === `${treeSelected.id}`
      )
      policy_exception_trees = {
        assign_tree_id: treeSelected.id,
        policy_approval_rule_id: selectedRule.policy_approval_rule_id,
        tree_exception_tree_name: treeSelected.name
      }
    } else {
      found = selectedRule.policy_conference_exception_trees.find(
        node => `${node.assign_tree_id}` === `${treeSelected.id}`
      )
      policy_exception_trees = {
        assign_tree_id: treeSelected.id,
        policy_conference_rule_id: selectedRule.policy_conference_rule_id,
        tree_exception_tree_name: treeSelected.name
      }
    }

    if (found) {
      content = t('web.policies-flow.tree-exception-rule.exclude', {
        value: treeSelected?.name || ''
      })
      policy_exception_trees.remove = true
    } else {
      content = t('web.policies-flow.tree-exception-rule.include', {
        value: treeSelected?.name || ''
      })
    }

    const cancelText = t('common.actions.cancel')
    const okText = t('common.actions.yes')

    Modal.confirm({
      cancelText,
      content,
      okText,
      onCancel() {},
      onOk: () => {
        PoliciesService.saveExceptionTree(policy_exception_trees)
          .then(response => {
            if (response.status === 200) {
              toggle(true)
            } else {
              notification.error({
                message: t('common.response.status-500'),
                placement: 'bottomRight'
              })
            }
          })
          .catch(response => {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          })
      },
      title: t('web.policies-flow.tree-exception-rule.sure')
    })
  }

  return (
    <Modal
      centered
      closable
      destroyOnClose
      okButtonProps={{
        hidden: true,
        //@ts-ignore
        'data-test-id': 'hierarchy-flows-approvals-exception-rule'
      }}
      onCancel={() => {
        toggle()
      }}
      title={t('web.policies-flow.tree-exception-rule.title')}
      visible={Boolean(selectedRule)}
      width={800}
    >
      <Row>
        <CSS.Col>
          <CSS.Icon1 />
          {t('web.policies-flow.tree-exception-rule.subtitle1')}
        </CSS.Col>
        <CSS.Col>
          <CSS.Icon2 />
          {t('web.policies-flow.tree-exception-rule.subtitle2')}
        </CSS.Col>
        <CSS.Col>
          <CSS.Icon3 />
          {t('web.policies-flow.tree-exception-rule.subtitle3')}
        </CSS.Col>
      </Row>
      <Row>
        <CSS.Col>
          <CSS.Text>
            {t('web.policies-flow.tree-exception-rule.selected-rule')}
          </CSS.Text>
        </CSS.Col>
      </Row>
      {selectedRule && (
        <Row>
          <CSS.Col>
            <CSS.SelectedRule>
              {`${selectedRule.code} ${selectedRule.name}`}
            </CSS.SelectedRule>
          </CSS.Col>
        </Row>
      )}
      <Row>
        <Col span={24}>
          <CSS.Title>
            {t('web.policies-flow.tree-exception-rule.structure-exception')}
          </CSS.Title>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Tree setTreeSelected={setTreeSelected} valuedNodes={valuedNodes} />
        </Col>
      </Row>
    </Modal>
  )
}
